import React, { useCallback, useState } from "react";
import Layout from "@/components/Layout/Layout";
import { withAdmin } from "@/services/withAdmin";
import { IAdminFireBase } from "@/store/models/admin.models";
import { Button, Pagination, Skeleton } from "@mui/material";
import videoIcon from "@/assets/icons/music.svg";
import useItemsPerPage from "@/hooks/useItemsPerPage";
import styles from "./SongsContent.module.scss";
import { AudioCard } from "@/components/AudioCard/AudioCard";
import DeleteModal from "@/components/DeleteModal/DeleteModal";
import { AddFeaturedItem } from "../AddFeaturedItem/AddFeaturedItem";
import { useDeleteMediaMutation, useGetRecommendedMediaQuery } from "@/store/rtk-api/media.api";
import { IMedia } from "@/store/models/video.models";

const SongsContentCmp: React.FC<{ admin: IAdminFireBase }> = ({ admin }) => {
  const [page, setPage] = useState(1);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [isOpenAddItem, setIsOpenAddItem] = useState(false);
  const [selectedMusic, setSelectedMusic] = useState<IMedia | null>(null);
  const itemsPerPage = useItemsPerPage(2, 4, 6, 1164, 1680);

  const { data: musicData, isLoading } = useGetRecommendedMediaQuery({
    mediaType: "audio",
  });

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleToggleModal = () => {
    setIsOpenAddItem((prev) => !prev);
  };

  const [deleteMusicByUid] = useDeleteMediaMutation();

  const handleToggleDeleteModal = (music: IMedia) => {
    setSelectedMusic(music);
    setIsOpenDeleteModal(true);
  };

  const handleDeleteMusic = useCallback(async () => {
    if (selectedMusic) {
      await deleteMusicByUid(selectedMusic.uid);
      setIsOpenDeleteModal(false);
    }
  }, [selectedMusic, deleteMusicByUid]);

  const audioDataFiltered = musicData?.filter((music) =>
    music.sections.includes("Recommendation")
  );

  return (
    <Layout admin={admin}>
      <div className={styles.topSection}>
        <div className={styles.topSectionTitle}>
          <p>Songs</p>
          <img src={videoIcon} alt="video" />
        </div>
        <Button
          sx={{
            textTransform: "capitalize",
            height: "44px",
            width: "174px",
            backgroundColor: "#008000",
            borderRadius: "12px",
            "&:hover": {
              backgroundColor: "#008000",
            },
          }}
          variant="contained"
          onClick={handleToggleModal}
        >
          Add item
        </Button>
      </div>

      <div className={styles.content}>
        <div className={styles.itemsContainer}>
          {isLoading &&
            Array(4)
              .fill(0)
              .map((_, index) => (
                <div className={styles.item} key={index}>
                  <div className={styles.itemInfoWrap}>
                    <Skeleton
                      variant="rectangular"
                      width={150}
                      height={150}
                      animation="wave"
                      sx={{ borderRadius: "8px" }}
                    />
                    <div className={styles.itemInfo}>
                      <Skeleton
                        variant="text"
                        width={100}
                        animation="wave"
                        sx={{ marginBottom: 1 }}
                      />
                      <Skeleton
                        variant="text"
                        width={150}
                        animation="wave"
                        sx={{ marginBottom: 1 }}
                      />
                    </div>
                  </div>
                  <div className={styles.itemBtns}>
                    <Skeleton
                      variant="circular"
                      width={40}
                      height={40}
                      animation="wave"
                      sx={{ marginRight: 1 }}
                    />
                    <Skeleton
                      variant="circular"
                      width={40}
                      height={40}
                      animation="wave"
                    />
                  </div>
                </div>
              ))}
          {audioDataFiltered && audioDataFiltered.length > 0 ? (
            audioDataFiltered
              .filter(
                (audio, index, self) =>
                  index === self.findIndex((v) => v.uid === audio.uid)
              )
              .map((audio) => (
                <AudioCard
                  key={audio.uid}
                  audio={audio}
                  handleToggleDeleteModal={handleToggleDeleteModal}
                  isEdit={false}
                  isFeatured
                />
              ))
          ) : (
            <p>No data yet</p>
          )}
        </div>
        {isOpenDeleteModal && (
          <DeleteModal
            isOpen={isOpenDeleteModal}
            setIsOpen={setIsOpenDeleteModal}
            name={selectedMusic ? selectedMusic?.title : ""}
            supportingText="Are you sure you want to delete this audio?"
            onDelete={handleDeleteMusic}
          />
        )}
        {isOpenAddItem && (
          <AddFeaturedItem
            isOpen={isOpenAddItem}
            setIsOpen={setIsOpenAddItem}
            type="music"
          />
        )}

        {audioDataFiltered && (
          <Pagination
            page={page}
            onChange={handlePageChange}
            count={Math.ceil(audioDataFiltered.length / itemsPerPage)}
          />
        )}
      </div>
    </Layout>
  );
};

export const SongsContent = withAdmin(SongsContentCmp);
SongsContentCmp.displayName = "SongsContentCmp";

